import React, { useState } from 'react';
import { Home, Users, Shield, Calendar, Award, Star, ArrowRight, CheckCircle } from 'lucide-react';

interface FamiliesLandingProps {
  onGetStarted: () => void;
  onEmailCapture: (email: string) => void;
}

const FamiliesLanding: React.FC<FamiliesLandingProps> = ({ onGetStarted, onEmailCapture }) => {
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      onEmailCapture(email);
      setEmailSubmitted(true);
      setEmail('');
    }
  };

  const familyBenefits = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Weather-Intelligent Coordination",
      description: "Automatically coordinates outdoor family tasks around weather patterns. Never waste family time on rained-out projects."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Seasonal Maintenance Intelligence",
      description: "HVAC, pest control, and garden care automatically scheduled by season. Prevents expensive emergency repairs."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Smart Supply Management",
      description: "Track maintenance supplies, cleaning products, and household essentials. Automatic reorder alerts prevent shortages."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Professional Contractor Network",
      description: "Instant access to vetted contractors for complex maintenance. Commission tracking and instant booking available."
    }
  ];

  const testimonials = [
    {
      name: "The Johnson Family",
      members: "Parents + 2 Adult Kids",
      quote: "The seasonal maintenance automation is incredible! Never missed our bi-annual HVAC service again, and the pest control scheduling keeps our home bug-free year-round. Saved over $4,000 in emergency repairs.",
      rating: 5,
      location: "Phoenix, AZ"
    },
    {
      name: "The Martinez Household", 
      members: "3 Generations, 7 Adults",
      quote: "Weather-intelligent scheduling transformed our family workflow. No more wasted Saturdays when projects get rained out. The system bundles all outdoor tasks on perfect weather days.",
      rating: 5,
      location: "San Antonio, TX"
    }
  ];

  const useCases = [
    {
      title: "Adult Children at Home",
      description: "Recent grads, students, or those saving for their first home can contribute meaningfully to household management.",
      scenarios: ["College students helping with family chores", "Adult children preparing for independence", "Recent graduates contributing to family responsibilities"]
    },
    {
      title: "Multi-Generational Living",
      description: "When grandparents, parents, and adult children share a home, everyone needs clear roles and responsibilities.",
      scenarios: ["Elderly parents contributing within their abilities", "Grandparents helping with household wisdom", "Shared cooking and maintenance responsibilities"]
    },
    {
      title: "Blended Families",
      description: "Step-parents, half-siblings, and complex family structures need organized task management.",
      scenarios: ["Clear expectations for all family members", "Fair task distribution across relationships", "Building unity through shared responsibilities"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <div className="flex justify-center items-center gap-2 mb-6">
            <Home className="w-8 h-8 text-blue-500" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Smart Family Home Management
            </h1>
            <Users className="w-8 h-8 text-purple-500" />
          </div>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional-grade household management with weather intelligence and seasonal automation. 
            Perfect for families who want their home to run like a well-managed property.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Professional-Grade Platform:</strong> HoneyDo Systems uses advanced weather intelligence, seasonal automation, 
                and smart supply tracking. Designed for families who want their home managed like a premium property.
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={onGetStarted}
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              Start Your Family Trial <ArrowRight className="w-5 h-5" />
            </button>
            
            <form onSubmit={handleEmailSubmit} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email for family tips"
                className="px-4 py-4 border-2 border-blue-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-400 outline-none"
                required
              />
              <button
                type="submit"
                className="bg-white border-2 border-blue-500 text-blue-500 hover:bg-blue-50 font-semibold py-4 px-6 rounded-lg transition-colors"
              >
                {emailSubmitted ? 'Thanks!' : 'Get Tips'}
              </button>
            </form>
          </div>

          <div className="flex justify-center items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Family Plan: $9.99/month
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              14-day free trial
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Unlimited family members
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Perfect for Modern Family Situations
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">{useCase.title}</h3>
                <p className="text-gray-600 mb-4">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.scenarios.map((scenario, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {scenario}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Family Benefits */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Family-Focused Features
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {familyBenefits.map((benefit, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-white/20">
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-full w-12 h-12 flex items-center justify-center text-blue-600 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold mb-3 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Real Family Stories */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Real Families, Real Success
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    <Home className="w-8 h-8" />
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-gray-800 text-lg">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.members}</div>
                    <div className="text-sm text-gray-500">{testimonial.location}</div>
                  </div>
                </div>
                
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-600 italic text-lg">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>

        {/* Family Pricing */}
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20">
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Family Plan: Built for Larger Households
          </h2>
          <p className="text-xl text-gray-600 mb-6">
            $9.99/month for unlimited family members. Less than a pizza night for ongoing family harmony.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 mb-2">Unlimited</div>
              <div className="text-sm text-gray-600">Family Members</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 mb-2">Smart</div>
              <div className="text-sm text-gray-600">Task Aging & Priorities</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600 mb-2">Contractor</div>
              <div className="text-sm text-gray-600">Referral Network</div>
            </div>
          </div>
          
          <button
            onClick={onGetStarted}
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
          >
            Start 14-Day Family Trial
          </button>
          
          <p className="text-sm text-gray-500 mt-4">
            No credit card required • Cancel anytime • All family members included
          </p>
        </div>
      </div>
    </div>
  );
};

export default FamiliesLanding;